package app

import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.engine.js.Js
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.request.get
import io.ktor.client.request.parameter
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import io.ktor.serialization.kotlinx.json.json
import kotlinx.browser.localStorage
import kotlinx.datetime.Clock
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlin.random.Random

@Serializable
data class CodeResponse(val code: String)

@Serializable
data class SessionPayload(val code: String, val movies: List<Movie>)

@Serializable
data class TmdbResult(val title: String, val year: Int? = null, val posterPath: String? = null)

/** Talks to the Ktor backend. Sessions are keyed by an 8-digit [code]. */
object Api {
    private val client = HttpClient(Js) {
        install(ContentNegotiation) { json(Json { ignoreUnknownKeys = true }) }
    }
    private const val BASE = "/api"

    suspend fun newCode(): String =
        client.post("$BASE/session").body<CodeResponse>().code

    suspend fun load(code: String): List<Movie> =
        client.get("$BASE/session/$code").body<SessionPayload>().movies

    suspend fun save(code: String, movies: List<Movie>) {
        client.put("$BASE/session/$code") {
            contentType(ContentType.Application.Json)
            setBody(SessionPayload(code, movies))
        }
    }

    /** Best TMDb match for [title], or null if not found / lookup disabled. */
    suspend fun tmdb(title: String): TmdbResult? {
        val resp = client.get("$BASE/tmdb") { parameter("q", title) }
        return if (resp.status == HttpStatusCode.OK) resp.body() else null
    }
}

/** Builds a TMDb image URL from a poster path, or null. */
object Poster {
    fun url(path: String?, size: String = "w342"): String? =
        path?.let { "https://image.tmdb.org/t/p/$size$it" }
}

/** Remembers the current code across page reloads. */
object LocalStore {
    private const val KEY = "randompicker.code"
    fun savedCode(): String? = localStorage.getItem(KEY)?.takeIf { it.length == 8 }
    fun saveCode(code: String) = localStorage.setItem(KEY, code)
}

object Ids {
    fun next(): String =
        Clock.System.now().toEpochMilliseconds().toString() + "-" + Random.nextInt(0, 1_000_000)
}
